/**
 * @module features/agent-evals/useAgentEvalRun
 *
 * React Query hook for one agent eval run (`GET /org/agent-evals/runs/:id`). The run row carries the
 * final `result`; the per-attempt answer, stdout tail and transcript only exist on the raw attempt rows.
 */
import { useQuery } from '@tanstack/react-query';
import type { AgentEvalRunResult, AgentEvalTranscriptEntry } from '@chatsdet/types/agent-eval-result';
import { API_URL } from '../../config';
import { toApiError } from '../../lib/api-error';

export type AgentEvalAttemptDetail = {
  caseKey: string;
  attemptIndex: number;
  answer: string | null;
  stdoutTail: string | null;
  transcript: AgentEvalTranscriptEntry[] | null;
};

export type AgentEvalRunDetail = {
  id: string;
  status: AgentEvalRunResult['status'];
  verdict: AgentEvalRunResult['verdict'];
  error: string | null;
  modelAlias: string;
  model: string;
  commitSha: string;
  result: AgentEvalRunResult | null; // null until the run completes
  attempts: AgentEvalAttemptDetail[];
};

type RawRun = Omit<AgentEvalRunDetail, 'attempts'> & {
  attempts: {
    attemptIndex: number;
    answer: string | null;
    stdoutTail: string | null;
    transcript: AgentEvalTranscriptEntry[] | null;
    case: { key: string };
  }[];
};

const isActive = (status: AgentEvalRunDetail['status']) => status === 'queued' || status === 'running';

const fetchAgentEvalRun = async (runId: string, withTranscript: boolean): Promise<AgentEvalRunDetail> => {
  const query = withTranscript ? '?transcript=1' : '';
  const res = await fetch(`${API_URL}/org/agent-evals/runs/${encodeURIComponent(runId)}${query}`, { credentials: 'include' });
  if (!res.ok) throw await toApiError(res, 'Failed to load eval run');
  const { attempts, ...run } = ((await res.json()) as { data: RawRun }).data;
  return {
    ...run,
    attempts: attempts.map((a) => ({
      caseKey: a.case.key,
      attemptIndex: a.attemptIndex,
      answer: a.answer,
      stdoutTail: a.stdoutTail,
      transcript: a.transcript,
    })),
  };
};

export function useAgentEvalRun(runId: string) {
  const queryKey = ['agent-evals', 'run', runId];
  // Explicit data type: the refetchInterval callback and the context-reading queryFn otherwise defeat inference ({}).
  return useQuery<AgentEvalRunDetail>({
    queryKey,
    // Poll while the run is still going.
    refetchInterval: (query) => (query.state.data && isActive(query.state.data.status) ? 5_000 : false),
    queryFn: async ({ client }) => {
      // The page shows no transcripts until the run finishes, so polls skip them (they exist for multi-turn evals only);
      // the poll that sees the run finish fetches them once.
      const previous = client.getQueryData<AgentEvalRunDetail>(queryKey);
      if (!previous || !isActive(previous.status)) return fetchAgentEvalRun(runId, true);
      const light = await fetchAgentEvalRun(runId, false);
      return isActive(light.status) ? light : fetchAgentEvalRun(runId, true);
    },
  });
}
